import { PointerEvent, useRef } from 'react'
import { VisualZoom } from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'

type Props = {
  image: HTMLImageElement | undefined
  aspect: number
  zoom: VisualZoom
  onZoomChange?: (zoom: VisualZoom) => void
  className?: string
}

const THUMB_WIDTH = 96

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const ZoomOverviewThumbnail = ({
  image,
  aspect,
  zoom,
  onZoomChange,
  className,
}: Props) => {
  const thumbRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    clientX: number
    clientY: number
    originX: number
    originY: number
  } | null>(null)

  if (zoom.scale <= 1) return null

  const thumbHeight = THUMB_WIDTH * aspect
  const viewportSize = 1 / zoom.scale
  const travel = 1 - viewportSize
  const left = zoom.x * travel
  const top = zoom.y * travel
  const canDrag = onZoomChange !== undefined && travel > 0

  const updateFromPointer = (clientX: number, clientY: number) => {
    if (!dragRef.current || !onZoomChange || !thumbRef.current) return
    const rect = thumbRef.current.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const dx = (clientX - dragRef.current.clientX) / rect.width
    const dy = (clientY - dragRef.current.clientY) / rect.height
    onZoomChange({
      ...zoom,
      x: clamp(dragRef.current.originX + dx / travel, 0, 1),
      y: clamp(dragRef.current.originY + dy / travel, 0, 1),
    })
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!canDrag) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      originX: zoom.x,
      originY: zoom.y,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    updateFromPointer(event.clientX, event.clientY)
  }

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    updateFromPointer(event.clientX, event.clientY)
    dragRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      ref={thumbRef}
      className={cn(
        'absolute bottom-2 right-2 z-30 overflow-hidden rounded border border-border shadow',
        !canDrag && 'pointer-events-none',
        className,
      )}
      style={{ width: THUMB_WIDTH, height: thumbHeight }}
    >
      {image ? (
        <img
          src={image.src}
          alt=''
          className='pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70'
          draggable={false}
        />
      ) : (
        <div className='pointer-events-none absolute inset-0 bg-muted opacity-70' />
      )}
      <div
        className={cn(
          'absolute border-2 border-accent-foreground bg-accent-foreground/15',
          canDrag && 'cursor-grab active:cursor-grabbing',
        )}
        style={{
          left: `${left * 100}%`,
          top: `${top * 100}%`,
          width: `${viewportSize * 100}%`,
          height: `${viewportSize * 100}%`,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
    </div>
  )
}

export default ZoomOverviewThumbnail
